import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload, ClipboardList, Code2, Zap, FileText,
  ShieldCheck, AlertTriangle, ArrowRight, Star,
  Lightbulb, CheckCircle2, Download,
} from "lucide-react";

const Tip = ({ children }) => (
  <div className="flex items-start gap-2.5 rounded-xl border border-icon-teal/25 bg-icon-teal/5 px-4 py-3">
    <Lightbulb className="w-4 h-4 text-icon-teal flex-shrink-0 mt-0.5" />
    <p className="text-sm text-brand-text leading-relaxed">{children}</p>
  </div>
);

const Warn = ({ children }) => (
  <div className="flex items-start gap-2.5 rounded-xl border border-warning/25 bg-warning/5 px-4 py-3">
    <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0 mt-0.5" />
    <p className="text-sm text-brand-text leading-relaxed">{children}</p>
  </div>
);

const Kbd = ({ children }) => (
  <kbd className="inline-flex items-center justify-center px-1.5 py-0.5 rounded bg-surface-border/80 text-brand-text text-[12px] font-mono font-semibold border border-surface-border shadow-sm leading-none">
    {children}
  </kbd>
);

const FINDINGS = [
  { risk: "High",   cls: "bg-danger/10 text-danger border-danger/20",       text: "Your data is sold to third-party ad networks" },
  { risk: "High",   cls: "bg-danger/10 text-danger border-danger/20",       text: "Indefinite data retention even after deletion" },
  { risk: "Medium", cls: "bg-warning/10 text-warning border-warning/20",    text: "Opt-out requires a written request by post" },
  { risk: "Low",    cls: "bg-success/10 text-success border-success/20",    text: "Cookie consent banner is provided" },
];

const INPUT_METHODS = [
  { id: "pdf",  label: "Upload PDF",   icon: FileText },
  { id: "text", label: "Copy & Paste", icon: ClipboardList },
  { id: "html", label: "Save as HTML", icon: Code2 },
];

/* ─── Step 1: Sample report preview ─────────────────────────────────────── */
const Step1 = () => (
  <div className="space-y-5">
    <p className="text-brand-muted text-sm leading-relaxed">
      AIPolicyScan reads every clause and gives you a plain-English risk report. Each finding
      is rated <strong className="text-danger">High</strong>,{" "}
      <strong className="text-warning">Medium</strong>, or{" "}
      <strong className="text-success">Low</strong> and linked back to the exact sentence
      so you can verify it yourself.
    </p>

    {/* Mock report card */}
    <div className="rounded-2xl border border-surface-border bg-white shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-surface-border bg-gradient-to-r from-icon-purple/5 to-icon-teal/5">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-icon-purple flex items-center justify-center">
            <ShieldCheck className="w-3.5 h-3.5 text-white" />
          </div>
          <div>
            <p className="text-sm font-bold text-brand-text">Risk Analysis Report</p>
            <p className="text-[11px] text-brand-muted">AIPolicyScan · example.com/privacy</p>
          </div>
        </div>
        <span className="text-[11px] font-bold px-2.5 py-1 rounded-full bg-danger/10 text-danger border border-danger/20">
          2 High · 1 Med
        </span>
      </div>

      <div className="p-4 space-y-2">
        {FINDINGS.map(({ risk, cls, text }, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: 6 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.08 * i }}
            className={`flex items-start gap-2.5 rounded-lg border px-3 py-2 text-xs ${cls}`}
          >
            <span className="font-bold whitespace-nowrap w-14 flex-shrink-0">{risk}</span>
            <span className="text-brand-text leading-snug">{text}</span>
          </motion.div>
        ))}
      </div>

      <div className="px-4 pb-4 flex items-center gap-3">
        <span className="text-xs text-brand-muted">Privacy score</span>
        <div className="flex-1 h-2 rounded-full bg-surface-border overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-danger to-warning"
            initial={{ width: 0 }}
            whileInView={{ width: "35%" }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: "easeOut" }}
          />
        </div>
        <span className="text-xs font-bold text-danger">35 / 100</span>
      </div>
    </div>

    {/* Download format grid */}
    <div>
      <p className="text-sm font-semibold text-brand-text mb-3">Download as:</p>
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "PDF Report", icon: FileText,      desc: "Branded one-pager" },
          { label: "JSON",       icon: Code2,         desc: "For integrations" },
          { label: "Summary",    icon: ClipboardList, desc: "Plain-text copy" },
        ].map(({ label, icon: Icon, desc }) => (
          <div
            key={label}
            className="flex flex-col items-center gap-1.5 rounded-xl border border-surface-border bg-surface p-3 hover:border-icon-purple hover:shadow-sm transition-all cursor-pointer group text-center"
          >
            <div className="w-8 h-8 rounded-lg bg-icon-purple/10 flex items-center justify-center group-hover:bg-icon-purple/20 transition-colors">
              <Icon className="w-4 h-4 text-icon-purple" />
            </div>
            <span className="text-xs font-semibold text-brand-text">{label}</span>
            <span className="text-[11px] text-brand-muted">{desc}</span>
          </div>
        ))}
      </div>
    </div>

    <Tip>Reports are saved in your account for 90 days — download again any time without re-scanning.</Tip>
  </div>
);

/* ─── Step 2: Find the policy page ──────────────────────────────────────── */
const Step2 = () => (
  <div className="space-y-4">
    <p className="text-brand-muted text-sm leading-relaxed">
      Go to the website you want to check. Scroll to the very bottom — the footer almost always
      has a link called{" "}
      <span className="text-icon-teal font-medium">Privacy Policy</span>,{" "}
      <span className="text-icon-teal font-medium">Terms of Service</span>, or{" "}
      <span className="text-icon-teal font-medium">Cookie Policy</span>. Open that page.
    </p>

    {/* Browser footer mockup */}
    <div className="rounded-xl border border-surface-border bg-surface overflow-hidden">
      <div className="px-4 py-2 bg-surface-border/30 border-b border-surface-border flex items-center gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-danger/50" />
        <div className="w-2.5 h-2.5 rounded-full bg-warning/50" />
        <div className="w-2.5 h-2.5 rounded-full bg-success/50" />
        <div className="ml-2 flex-1 rounded bg-white h-4 flex items-center px-2 border border-surface-border">
          <span className="text-[10px] text-brand-muted font-mono">example.com/privacy</span>
        </div>
      </div>
      <div className="p-4 space-y-1.5">
        <div className="h-2 rounded bg-surface-border/60 w-full" />
        <div className="h-2 rounded bg-surface-border/60 w-5/6" />
        <div className="h-2 rounded bg-surface-border/60 w-4/6" />
      </div>
      <div className="px-4 pb-4 pt-2 border-t border-surface-border flex flex-wrap gap-3 items-center">
        <span className="text-[11px] text-brand-muted">2025 Example Inc.</span>
        {["Privacy Policy", "Terms of Service", "Cookie Policy"].map((l) => (
          <span
            key={l}
            className={`text-[11px] font-medium ${
              l === "Privacy Policy"
                ? "text-icon-teal underline underline-offset-2 animate-pulse"
                : "text-brand-muted"
            }`}
          >
            {l}
          </span>
        ))}
      </div>
    </div>

    <Tip>
      Cannot find it? Search{" "}
      <kbd className="px-1.5 py-0.5 rounded bg-surface-border/80 font-mono text-[12px] border border-surface-border">
        site:example.com privacy policy
      </kbd>{" "}
      in Google.
    </Tip>
  </div>
);

/* ─── Step 3: Grab the policy text ──────────────────────────────────────── */
const Step3 = ({ activeMethod, setActiveMethod }) => (
  <div className="space-y-4">
    <p className="text-brand-muted text-sm leading-relaxed">
      Pick the method that works best for the site. All three give the same quality analysis.
    </p>

    {/* Pill tabs */}
    <div className="flex flex-wrap gap-2">
      {INPUT_METHODS.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          onClick={() => setActiveMethod(id)}
          className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
            activeMethod === id
              ? "bg-icon-purple text-white border-icon-purple shadow-sm"
              : "bg-white text-brand-muted border-surface-border hover:border-icon-purple hover:text-icon-purple"
          }`}
        >
          <Icon className="w-3.5 h-3.5" />
          {label}
        </button>
      ))}
    </div>

    <AnimatePresence mode="wait">
      {activeMethod === "pdf" && (
        <motion.div
          key="pdf"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="space-y-3"
        >
          <ol className="space-y-3 list-none">
            {[
              "Open the privacy policy page in your browser.",
              <span key="b">Press <Kbd>Cmd P</Kbd> (Mac) or <Kbd>Ctrl P</Kbd> (Windows) to open Print.</span>,
              <span key="c">Set destination to <strong className="text-brand-text">Save as PDF</strong> and click Save.</span>,
              "Upload the saved file in Step 4 below.",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-brand-muted">
                <span className="w-5 h-5 rounded-full bg-icon-purple/10 text-icon-purple text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span className="leading-snug">{t}</span>
              </li>
            ))}
          </ol>
          <Tip>Works in every browser — no extra software needed.</Tip>
        </motion.div>
      )}

      {activeMethod === "text" && (
        <motion.div
          key="text"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="space-y-3"
        >
          <ol className="space-y-3 list-none">
            {[
              "Open the privacy policy page.",
              <span key="b">Press <Kbd>Cmd A</Kbd> / <Kbd>Ctrl A</Kbd> to select all, then <Kbd>Cmd C</Kbd> / <Kbd>Ctrl C</Kbd> to copy.</span>,
              "Paste into the text box in Step 4 below.",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-brand-muted">
                <span className="w-5 h-5 rounded-full bg-icon-purple/10 text-icon-purple text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span className="leading-snug">{t}</span>
              </li>
            ))}
          </ol>
          <Warn>Copy-paste can miss tables or footnotes. PDF or HTML gives more accurate results.</Warn>
        </motion.div>
      )}

      {activeMethod === "html" && (
        <motion.div
          key="html"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="space-y-3"
        >
          <ol className="space-y-3 list-none">
            {[
              "Open the policy page in Chrome or Edge.",
              <span key="b">Press <Kbd>Cmd S</Kbd> / <Kbd>Ctrl S</Kbd>.</span>,
              <span key="c">Set Format to <strong className="text-brand-text">Webpage, Complete</strong> and save.</span>,
              "Upload the .html file in Step 4 below.",
            ].map((t, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-brand-muted">
                <span className="w-5 h-5 rounded-full bg-icon-purple/10 text-icon-purple text-[11px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <span className="leading-snug">{t}</span>
              </li>
            ))}
          </ol>
          <Tip>HTML retains all formatting and hidden sections that copy-paste misses.</Tip>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

/* ─── Step 4: Upload, analyse, download ──────────────────────────────────── */
const Step4 = ({ activeMethod, uploadClicked, setUploadClicked }) => (
  <div className="space-y-4">
    <p className="text-brand-muted text-sm leading-relaxed">
      Upload or paste your policy document, click{" "}
      <strong className="text-brand-text">Analyse</strong>, and your report is ready in under
      10 seconds. Then download it in whatever format you need.
    </p>

    {/* Upload zone or textarea */}
    {activeMethod !== "text" ? (
      <motion.div
        onClick={() => setUploadClicked((v) => !v)}
        animate={
          uploadClicked
            ? { borderColor: "#8B5CF6", backgroundColor: "#F5F3FF" }
            : { borderColor: "#E2E8F0", backgroundColor: "#F8FAFC" }
        }
        whileTap={{ scale: 0.985 }}
        transition={{ duration: 0.2 }}
        className="rounded-2xl border-2 border-dashed p-8 text-center cursor-pointer hover:shadow-sm transition-shadow"
      >
        <AnimatePresence mode="wait">
          {uploadClicked ? (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.88 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-2"
            >
              <div className="w-11 h-11 rounded-full bg-icon-teal/10 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-icon-teal" />
              </div>
              <p className="text-sm font-semibold text-icon-teal">privacy_policy.pdf selected</p>
              <p className="text-xs text-brand-muted">Click to replace</p>
            </motion.div>
          ) : (
            <motion.div
              key="idle"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex flex-col items-center gap-2"
            >
              <div className="w-11 h-11 rounded-full bg-icon-purple/10 flex items-center justify-center">
                <Upload className="w-5 h-5 text-icon-purple" />
              </div>
              <p className="text-sm font-medium text-brand-text">
                Drop your {activeMethod === "html" ? "HTML" : "PDF"} here or{" "}
                <span className="text-icon-purple underline underline-offset-2">browse</span>
              </p>
              <p className="text-xs text-brand-muted">
                {activeMethod === "html" ? ".html" : "PDF"} up to 50 MB
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    ) : (
      <textarea
        rows={5}
        placeholder="Paste your privacy policy text here..."
        className="w-full rounded-2xl border-2 border-dashed border-surface-border bg-surface p-4 text-sm text-brand-text placeholder:text-brand-muted focus:outline-none focus:border-icon-purple focus:ring-2 focus:ring-icon-purple/20 resize-none transition"
      />
    )}

    {/* Analyse button */}
    <motion.button
      whileHover={{ scale: 1.015 }}
      whileTap={{ scale: 0.97 }}
      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-icon-purple text-white text-sm font-semibold shadow-md shadow-violet-200 hover:bg-violet-600 transition-colors"
    >
      <Zap className="w-4 h-4" /> Analyse Now
    </motion.button>

    {/* Download strip */}
    <div className="rounded-xl border border-surface-border bg-surface overflow-hidden">
      <div className="px-4 py-2.5 border-b border-surface-border flex items-center gap-2">
        <Download className="w-3.5 h-3.5 text-icon-teal" />
        <p className="text-xs font-semibold text-brand-text">Download your report after analysis</p>
      </div>
      <div className="divide-y divide-surface-border">
        {[
          { label: "PDF Report",    desc: "Branded one-pager, ready to share",          icon: FileText },
          { label: "JSON Export",   desc: "Machine-readable for tools and integrations", icon: Code2 },
          { label: "Plain Summary", desc: "Copy-paste into email or Slack",             icon: ClipboardList },
        ].map(({ label, desc, icon: Icon }) => (
          <div
            key={label}
            className="flex items-center gap-3 px-4 py-2.5 hover:bg-icon-purple/5 transition-colors cursor-pointer group"
          >
            <div className="w-7 h-7 rounded-lg bg-icon-purple/10 flex items-center justify-center flex-shrink-0 group-hover:bg-icon-purple/20 transition-colors">
              <Icon className="w-3.5 h-3.5 text-icon-purple" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-brand-text">{label}</p>
              <p className="text-[11px] text-brand-muted">{desc}</p>
            </div>
            <ArrowRight className="w-3.5 h-3.5 text-brand-muted group-hover:text-icon-purple transition-colors flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>

    <Tip>Reports stay in your account for 90 days — re-download any time without re-scanning.</Tip>
  </div>
);

/* ─── Step metadata ──────────────────────────────────────────────────────── */
const STEPS_META = [
  { id: 1, label: "Step 1", title: "See what you'll get",          subtitle: "A risk report you can actually act on" },
  { id: 2, label: "Step 2", title: "Find the privacy policy page", subtitle: "Locate the document on the website" },
  { id: 3, label: "Step 3", title: "Grab the policy text",         subtitle: "PDF, copy-paste, or save as HTML" },
  { id: 4, label: "Step 4", title: "Upload, analyse and download", subtitle: "Done in under 10 seconds" },
];

/* ─── Main section ───────────────────────────────────────────────────────── */
const HowToDoSection = () => {
  const [activeMethod, setActiveMethod]   = useState("pdf");
  const [uploadClicked, setUploadClicked] = useState(false);

  const renderStep = (id) => {
    if (id === 1) return <Step1 />;
    if (id === 2) return <Step2 />;
    if (id === 3) return <Step3 activeMethod={activeMethod} setActiveMethod={setActiveMethod} />;
    return <Step4 activeMethod={activeMethod} uploadClicked={uploadClicked} setUploadClicked={setUploadClicked} />;
  };

  return (
    <section id="how-to-do" className="py-24 bg-surface relative overflow-hidden">
      {/* Decorative blobs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-icon-purple/5 blur-3xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-icon-teal/5 blur-3xl translate-y-1/3 -translate-x-1/4" />
      </div>

      <div className="max-w-2xl mx-auto px-6 relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-icon-purple/10 text-icon-purple text-xs font-semibold tracking-wide uppercase mb-4">
            <Star className="w-3 h-3" /> How to use AIPolicyScan
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-brand-text mb-4">
            From policy page to risk report
            <br className="hidden sm:block" /> in four steps
          </h2>
          <p className="text-brand-muted text-base leading-relaxed max-w-lg mx-auto">
            Scroll through at your own pace — everything is visible, nothing to click open.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical gradient line */}
          <div className="absolute left-[19px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-icon-purple/40 via-primary/20 to-icon-teal/40" />

          <div className="space-y-12">
            {STEPS_META.map((step, idx) => (
              <motion.div
                key={step.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ delay: idx * 0.05, duration: 0.45 }}
                className="flex gap-6"
              >
                {/* Circle number */}
                <div className="flex-shrink-0" style={{ width: 40 }}>
                  <div className="w-10 h-10 rounded-full bg-icon-purple flex items-center justify-center shadow-md shadow-violet-200 relative z-10">
                    <span className="text-white text-sm font-bold">{step.id}</span>
                  </div>
                </div>

                {/* Card */}
                <div className="flex-1 pb-2">
                  <div className="mb-3">
                    <span className="text-[11px] font-bold text-icon-purple/60 uppercase tracking-wider">
                      {step.label}
                    </span>
                    <h3 className="text-lg font-bold text-brand-text mt-0.5">{step.title}</h3>
                    <p className="text-sm text-brand-muted">{step.subtitle}</p>
                  </div>
                  <div className="rounded-2xl border border-surface-border bg-white shadow-sm p-5">
                    {renderStep(step.id)}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToDoSection;
